if (!window.Maximize.loadedScript.includes("header.js")) {
  window.Maximize.loadedScript.push("header.js");

  requestAnimationFrame(() => {
    document.addEventListener("alpine:init", () => {

      /* ──────────────────────────────────────────────────────────────
       * UTILITY: shared debounce (single definition, reused by all)
       * ────────────────────────────────────────────────────────────── */
      function debounce(fn, delay) {
        let timer;
        return function (...args) {
          clearTimeout(timer);
          timer = setTimeout(() => fn.apply(this, args), delay);
        };
      }

      /* ──────────────────────────────────────────────────────────────
       * UTILITY: detect touch capability once
       * ────────────────────────────────────────────────────────────── */
      const isTouchDevice = Boolean(
        "ontouchstart" in window
        || window.DocumentTouch && document instanceof DocumentTouch
        || navigator.maxTouchPoints
        || navigator.msMaxTouchPoints
      );

      /* ──────────────────────────────────────────────────────────────
       * UTILITY: calculate vertical offset for account popups
       * ────────────────────────────────────────────────────────────── */
      function calculateAccountIconOffset(iconSelector) {
        const icon =  document.querySelector(iconSelector);
        const rect = icon.getBoundingClientRect();
        const iconCenterY = rect.top + (rect.height / 2);
        const offsetFromViewportTop = Math.round(iconCenterY);
        return offsetFromViewportTop;
      }

      let _currentAccountObserver = null;
      function accountPopupListener(isMobile = false) {
        const selector = isMobile ? '.mobile-header-account-menu' : '.header-account-menu';
        const accountEl = document.querySelector(`${selector} shopify-account`);
        if (!accountEl) return;

        const handleStateChange = (isOpen, isMobile) => {
          if (isOpen) {
            document.body.style.setProperty('overflow', 'visible');
            if (isMobile) {
              const offsetMobile = calculateAccountIconOffset('.mobile-header-account-menu');
              document.body.style.setProperty('--account-offset-top-mobile', `${offsetMobile + ICON_POPUP_SPACING.MOBILE}px`);
            } else {
              const offsetDesktop = calculateAccountIconOffset('.header-account-menu');
              document.body.style.setProperty('--account-offset-top', `${offsetDesktop + ICON_POPUP_SPACING.DESKTOP}px`);
              Alpine.store("xHeaderMenu").hideMenu();
            }
          } else {
            ['--account-offset-top-mobile', '--account-offset-top', 'overflow'].forEach(prop => {
              document.body.style.removeProperty(prop);
            });
          }
        };

        if (_currentAccountObserver) {
          _currentAccountObserver.disconnect();
          _currentAccountObserver = null;
        }

        const observeShadowRoot = (shadowRoot) => {
          if (!shadowRoot) return;

          const dialog = shadowRoot.querySelector('dialog');
          if (!dialog) return;
          const dialogObserver = new MutationObserver(() => {
            handleStateChange(dialog.hasAttribute('open'), isMobile);
          });

          dialogObserver.observe(dialog, { attributes: true, attributeFilter: ['open'] });
          _currentAccountObserver = dialogObserver;
          handleStateChange(dialog.hasAttribute('open'), isMobile);
        };

        if (accountEl.shadowRoot) {
          observeShadowRoot(accountEl.shadowRoot);
        } 
      }

      /* ──────────────────────────────────────────────────────────────
       * STORE: xScreenMode
       *
       * Determines whether to render the desktop or mobile menu.
       * Uses matchMedia at the 1024px breakpoint (same as Tailwind lg:)
       * to completely avoid rendering the unused menu via x-if.
       *
       * In Shopify design mode, both menus render for editor compat.
       * ────────────────────────────────────────────────────────────── */
      Alpine.store("xScreenMode", {
        isDesktop: window.innerWidth >= 1024,
        showBoth: false,
        initialized: false,

        init() {
          // In design mode, render both menus so the editor can interact with them
          if (Shopify.designMode) {
            this.showBoth = true;
            this.isDesktop = window.innerWidth >= 1024;
            this.initialized = true;
            return;
          }

          const scheduleInit = window.requestIdleCallback || ((cb) => requestAnimationFrame(cb));
          scheduleInit(() => {
            const mq = window.matchMedia("(min-width: 1024px)");
            this.isDesktop = mq.matches;
            this.initialized = true;

            // Listen for breakpoint changes (e.g. orientation change)
            mq.addEventListener("change", (e) => {
              this.isDesktop = e.matches;
            });
          });
        },
      });

      /* ────────────────────────────────────────────────────────────────
       * STORE: xHeaderMenu
       *
       * Responsibilities:
       *   - Sticky header state management
       *   - Scroll-driven header show/hide
       *   - Transparent header behavior
       *   - Menu open/close toggling
       *   - CSS custom-property synchronization
       * ──────────────────────────────────────────────────────────────── */
      Alpine.store("xHeaderMenu", {

        // ── Public reactive state ──────────────────────────────────
        isSticky: false,
        isTouch: isTouchDevice,
        isTransparent: false,
        clickedHeader: false,
        showLogoTransparent: false,
        headerHeight: 0,
        collapseMenuHeight: 0,
        isCollapseMenu: false,

        // ── Config (set once during init) ──────────────────────────
        sectionId: "",
        stickyType: "none",
        logoPosition: null,

        // ── Private / internal state ───────────────────────────────
        _lastScrollTop: false,
        _previousScrollTop: 0,
        _themeModeChanged: false,
        _scrollTicking: false,

        // ── Cached DOM references ──────────────────────────────────
        _headerEl: null,
        _headerSectionEl: null,
        _drawerMenuEl: null,
        _headerTopEl: null,
        _stickyTargets: null,

        _headerBounds: { top: 0, bottom: 0 },

        /* ════════════════════════════════════════════════════════════
         * INITIALIZATION
         * ════════════════════════════════════════════════════════════ */

        handleInitHeader(sectionId, stickyType, isTransparent, logoPosition, isCollapseMenu) {
          // Store config
          this.sectionId = sectionId;
          this.stickyType = stickyType;
          this.isTransparent = isTransparent;
          this.logoPosition = logoPosition;
          this.showLogoTransparent = isTransparent;
          this.isCollapseMenu = isCollapseMenu

          // Cache primary DOM elements
          // this._headerEl = document.getElementById("x-header-content");
          this._headerSectionEl = document.getElementById(sectionId);
          this._drawerMenuEl = document.getElementById("DrawerMenu");
          this._headerTopEl = this._headerSectionEl.querySelector(".header-top");
          this._cacheStickyTargets();

          // Initial measurements
          this._updateViewportHeight();
          this.updateHeaderBottom();
          accountPopupListener(false);

          if (stickyType !== "none") {
            this._applyStickyState(window.scrollY);
            this._updateAnnouncementHeight();
            this._headerSectionEl.classList.add("sticky-header");
            Alpine.nextTick(() => {
              requestIdleCallback(() => this._measureCollapseMenuHeight())
              this._measureHeaderHeight();
              this._syncHeaderHeightVar();
            });
          }

          // Respond to viewport changes
          const onResize = debounce((event) => {
            if (event.detail?.isChangeLayoutMode) return;

            if (stickyType !== "none") {
              this._measureCollapseMenuHeight()
              this._measureHeaderHeight();
              this._updateAnnouncementHeight();
              this._syncHeaderHeightVar();
              this._syncTopStickyVar();
            }
            this._updateViewportHeight();
            this.updateHeaderBottom();
            accountPopupListener(window.innerWidth < MIN_DEVICE_WIDTH.desktop);
          }, 100);

          window.addEventListener("resize", onResize);

          // Capture initial bounds via IntersectionObserver (non-blocking)
          this._observeInitialBounds(this._headerSectionEl);
        },

        /* ════════════════════════════════════════════════════════════
         * SCROLL HANDLING (unified, rAF-throttled)
         * ════════════════════════════════════════════════════════════ */

        /**
         * Single entry-point for all scroll work.
         * Called from the Alpine `@scroll.window.passive` directive.
         * Ensures only one rAF callback runs per frame.
         */
        onScroll() {
          if (this._scrollTicking) return;
          this._scrollTicking = true;

          requestAnimationFrame(() => {
            this._scrollTicking = false;

            if (!this._headerSectionEl) return;

            const scrollTop = window.scrollY;

            this._applyStickyState(scrollTop);
            this._applyScrollDirection(scrollTop);
            this._syncTopStickyVar();

            this._themeModeChanged = false;
          });
        },

        /** Determine sticky / transparent state based on scroll position. */
        _applyStickyState(scrollTop) {
          const { top, bottom } = this._headerBounds;
          if (scrollTop > bottom) {
            this.isSticky = true;
            if (this.isTransparent) this.showLogoTransparent = false;

            if (scrollTop <= this._previousScrollTop) {
              this.clickedHeader = false;
            }

            const isAlwaysType = this.stickyType === "always" || this.stickyType === "reduce-logo-size";
            if (scrollTop > this._previousScrollTop && isAlwaysType) {
              this._headerSectionEl.classList.add("always-animation", this.stickyType);
            }
          } else if (scrollTop <= top) {
            this._headerSectionEl?.classList.remove(this.stickyType, "always-animation");
            this.clickedHeader = false;
            this.isSticky = false;
            if (this.isTransparent) this.showLogoTransparent = true;
          }

          // Recalculate height for transparent headers (layout may shift)
          if (this.isTransparent && this.stickyType !== "on-scroll-up") {
            this._measureHeaderHeight();
            this._syncHeaderHeightVar();
          }

          this._previousScrollTop = Math.max(scrollTop, 0);
        },

        /** Show/hide header based on scroll direction (scroll-up reveal). */
        _applyScrollDirection(scrollTop) {
          if (this._lastScrollTop === false) {
            this._lastScrollTop = scrollTop;
            return;
          }

          const delta = Math.abs(scrollTop - this._lastScrollTop);
          if (delta <= 10) return; // ignore micro-scrolls

          const isUp = scrollTop < this._lastScrollTop;
          const hideClass = this.stickyType === "on-scroll-up" ? "header-up" : (this.isCollapseMenu ? "header-up-always" : "not-scroll-up");

          if (isUp) {
            this._headerSectionEl.classList.remove(hideClass);
            if (this.isTransparent) this._measureHeaderHeight();
            this._syncHeaderHeightVar(false);
          } else if (!this._themeModeChanged) {
            if (this.isTransparent) this._measureHeaderHeight();
            this._headerSectionEl.classList.add(hideClass);
            this.stickyType === "on-scroll-up" ? this._syncHeaderHeightVar(true) : this._syncHeaderHeightVar(this.isCollapseMenu);
          }

          this._lastScrollTop = scrollTop;
        },

        /* ════════════════════════════════════════════════════════════
         * DOM MEASUREMENT (reads)
         * ════════════════════════════════════════════════════════════ */

        _measureHeaderHeight() {
          if (this._headerSectionEl && this.stickyType !== "none") {
            if (this.isTransparent) {
              this.headerHeight = this._headerSectionEl.querySelector("#x-header-content").getBoundingClientRect().height;
            } else {
              this.headerHeight = this._headerSectionEl.getBoundingClientRect().height;
            }
          }
        },
        _measureCollapseMenuHeight() {
          if (this.stickyType != "on-scroll-up" && this.isCollapseMenu) {
            const collapseMenuEl = this._headerSectionEl?.querySelector(".header-collapse-menu > div");
            this.collapseMenuHeight = collapseMenuEl.getBoundingClientRect().height
          } 
        },

        _observeInitialBounds(element) {
          const observer = new IntersectionObserver((entries) => {
            this._headerBounds = entries[0].intersectionRect;
            observer.disconnect();
          });
          observer.observe(element);
        },

        /* ════════════════════════════════════════════════════════════
         * CSS CUSTOM-PROPERTY SYNC (writes)
         * ════════════════════════════════════════════════════════════ */

        /** Propagate --height-header to header and all sticky-aware sections. */
        _syncHeaderHeightVar(isHidden = false) {
          let value = (isHidden || this.showLogoTransparent) ? "0px" : `${this.headerHeight}px`;
          if (isHidden && this.isCollapseMenu) {
            value = `${this.headerHeight - this.collapseMenuHeight}px`
          } 

          // Write only if changed
          if (this._headerSectionEl?.dataset.heightHeader === value) return;

          this._headerSectionEl?.style.setProperty("--height-header", value);
          this._headerSectionEl && (this._headerSectionEl.dataset.heightHeader = value);

          if (this._stickyTargets) {
            for (let i = 0; i < this._stickyTargets.length; i++) {
              this._stickyTargets[i].style.setProperty("--height-header", value);
            }
          }
        },

        _syncTopStickyVar() {
          const value = `${this.headerHeight}px`;

          if (this._headerSectionEl?.dataset.topHeader === value) return;

          this._headerSectionEl.style.setProperty("--top-header", value);
          this._headerSectionEl.dataset.topHeader = value;
        },

        _updateAnnouncementHeight() {
          requestAnimationFrame(() => {
            const announcement = document.getElementById("x-announcement");
            if (!announcement) return;
            const isSticky = announcement?.dataset.isSticky === "true";
            const height = isSticky
              ? `${announcement.getBoundingClientRect().height.toFixed(2)}px`
              : "0px";

            document.documentElement.style.setProperty("--announcement-height", height);
          });
        },

        _updateViewportHeight() {
          if (CSS.supports("height", "100dvh")) return;

          const root = document.documentElement;
          const height = `${window.innerHeight}px`;

          if (root.dataset.viewportHeight !== height) {
            root.dataset.viewportHeight = height;
            root.style.setProperty("--viewport-height", height);
          }
        },

        /** Cache elements that need --height-header updates. */
        _cacheStickyTargets() {
          this._stickyTargets = document.querySelectorAll([
            ".section-announcement:has([data-is-sticky='true'])",
            ".sticky-filter-top",
            ".sticky-top",
            ".sticky-filter-above-top",
            ".sticky-filter-aside-content-top",
            ".sticky-main-product-top",
            ".section-product-bundle",
          ].join(","));
        },

        /* ════════════════════════════════════════════════════════════
         * HEADER BOTTOM POSITION
         * ════════════════════════════════════════════════════════════ */

        updateHeaderBottom(delay = 0) {
          if (delay > 0) {
            setTimeout(() => this._writeHeaderBottom(), delay);
          } else {
            this._writeHeaderBottom();
          }
        },

        _writeHeaderBottom() {
          requestAnimationFrame(() => {
            if (!this._headerSectionEl) return;
            // Batch reads
            const headerRect = this.isTransparent ? document.getElementById("x-header-content")?.getBoundingClientRect() : this._headerSectionEl.getBoundingClientRect();
            const newBottom = `${(headerRect.bottom.toFixed(2) - 1)}px`;

            // Write only if changed (dataset cache)
            if (this._headerSectionEl.dataset.headerBottom !== newBottom) {
              this._headerSectionEl.style.setProperty("--header-bottom-position", newBottom);
              this._headerSectionEl.dataset.headerBottom = newBottom;
            }

            // Drawer menu position (non-compact layouts)
            if (this._drawerMenuEl && this.logoPosition !== "compact" && this._headerTopEl) {
              const newDrawerBottom = `${this._headerTopEl.getBoundingClientRect().bottom + 12}px`;
              if (this._drawerMenuEl.dataset.headerBottom !== newDrawerBottom) {
                this._drawerMenuEl.style.setProperty("--header-bottom-position", newDrawerBottom);
                this._drawerMenuEl.dataset.headerBottom = newDrawerBottom;
              }
            }
          });
        },

        /* ════════════════════════════════════════════════════════════
         * MENU INTERACTION
         * ════════════════════════════════════════════════════════════ */

        selectItem(el, type, isSub, event) {
          if (type === "menu" && Alpine.store("xDrawerMenu").menuOpen) {
            Alpine.store("xDrawerMenu").handleCloseMenu();
          }
          this.updateHeaderBottom();

          const selector = isSub ? ".toggle-menu-sub" : ".toggle-menu";

          // Prevent navigation on first click if dropdown exists
          if (event?.type === "click") {
            const parentItem = el.closest(".menu-item");
            const dropdown = parentItem?.querySelector(selector);
            if (dropdown && !dropdown.classList.contains("is-open")) {
              event.preventDefault();
            }
          }

          // Close all sibling menus
          const scope = isSub ? el.closest(".toggle-menu") : document;
          this._closeMenuItems(scope.querySelectorAll(selector));

          // Open the target menu
          const toggleMenu = el.querySelector(selector);
          if (toggleMenu) {
            toggleMenu.classList.add("is-open");
            toggleMenu.removeAttribute("inert");
          }

          if (el.closest(".product-menu-item__lv0")) {
            this._resizeProductMenu(el);
          }
        },

        handleKeyupElement(el) {
          const toggleMenu = el.querySelector(".toggle-menu");
          const openMenus = document.getElementsByClassName("toggle-menu is-open");

          for (let i = 0; i < openMenus.length; i++) {
            if (openMenus[i] !== toggleMenu) {
              openMenus[i].setAttribute("inert", "");
              openMenus[i].classList.remove("is-open");
            }
          }

          toggleMenu.classList.toggle("is-open");
          toggleMenu.toggleAttribute("inert");

          if (el.closest(".product-menu-item__lv0")) {
            this._resizeProductMenu(el);
          }
        },

        hideMenu(parentClass) {
          const selector = parentClass
            ? `.${parentClass} .toggle-menu.is-open`
            : ".toggle-menu.is-open";
          this._closeMenuItems(document.querySelectorAll(selector));
        },

        /** Bulk-close a NodeList of menu items. */
        _closeMenuItems(items) {
          for (let i = 0; i < items.length; i++) {
            items[i].classList.remove("is-open");
            items[i].setAttribute("inert", "");
          }
        },

        /* ════════════════════════════════════════════════════════════
         * PRODUCT MEGA-MENU HEIGHT
         * ════════════════════════════════════════════════════════════ */

        _resizeProductMenu(el) {
          const container = el.closest(".product-menu-item__lv0");
          if (!container) return;

          const megaMenu = container.querySelector(".product-mega-menu");
          const activeSubmenu = container.querySelector(".product-mega-menu_wrapper.is-open .mega-sub-container");
          const listMenu = container.querySelector(".product-list-item");

          requestAnimationFrame(() => {
            const submenuH = (activeSubmenu?.scrollHeight ?? 0) + 35;
            const listH = (listMenu?.scrollHeight ?? 0) + 68;
            const maxH = Math.min(Math.max(submenuH, listH), window.innerHeight);

            if (megaMenu) {
              megaMenu.style.height = `${maxH}px`;
            }
          });
        },

        /* ════════════════════════════════════════════════════════════
         * FOCUS & ACCESSIBILITY
         * ════════════════════════════════════════════════════════════ */

        focusFirstItem(el) {
          el.closest(".first-menu-item")
            ?.querySelector(".menu-parent-link span:last-of-type")
            ?.focus();
        },

        handleFocusOut(el, event) {
          if (event.relatedTarget && !el.contains(event.relatedTarget)) {
            this.hideMenu("main-menu-nav");
          }
        },

        /* ════════════════════════════════════════════════════════════
         * TOUCH HANDLING
         * ════════════════════════════════════════════════════════════ */

        touchItem(el, type, isSub = false) {
          if (el.dataset.touchEvent) return;

          el.dataset.touchEvent = "true";
          const touchClass = isSub ? "touched-sub" : "touched";

          el.addEventListener("touchend", (e) => {
            if (el.classList.contains(touchClass)) return;

            e.preventDefault();

            // Remove touch class from all items
            const touched = document.querySelectorAll(`.${touchClass}`);
            for (let i = 0; i < touched.length; i++) {
              touched[i].classList.remove(touchClass);
            }
            const menuItem = el.closest("li");

            el.classList.add(touchClass);
            if (Alpine.$data(el).firstLoad) {
              Alpine.$data(el).firstLoad();
            }
            this.selectItem(menuItem, type, isSub);
          }, { passive: false });
        },

        /* ════════════════════════════════════════════════════════════
         * TRANSPARENT HEADER HOVER
         * ════════════════════════════════════════════════════════════ */

        addTransparentHover(el) {
          el.classList.add("sticky-header-active");
        },

        removeTransparentHover(el) {
          el.classList.remove("sticky-header-active");
        },

        /* ════════════════════════════════════════════════════════════
         * THEME MODE
         * ════════════════════════════════════════════════════════════ */

        handleChangeThemeMode() {
          this._themeModeChanged = true;
          setTimeout(() => {
            this._measureHeaderHeight();
            this.updateHeaderBottom();
            this._syncHeaderHeightVar();
          }, 100);
        }
      });


      /* ────────────────────────────────────────────────────────────────
       * STORE: xMobileNav
       *
       * Responsibilities:
       *   - Mobile navigation drawer open/close
       *   - Active menu link stack management
       *   - Utility bar DOM relocation
       * ──────────────────────────────────────────────────────────────── */
      Alpine.store("xMobileNav", {
        show: false,
        loading: false,
        currentMenuLinks: [],

        init() {
          installMediaQueryWatcher("(min-width: 1024px)", () => this.close());
        },

        toggle() {
          this.show = !this.show;

          if (this.show) {
            Alpine.nextTick(() => {
              requestAnimationFrame(() => {
                Alpine.store("xHeaderMenu").updateHeaderBottom();
                accountPopupListener(true);
                setTimeout(() => {
                  Alpine.store("xFocusElement").trapFocus("x-header-mobile", "menu-navigation");
                }, 300);
              });
            });
          }

          document.body.classList.toggle("overflow-hidden", this.show);
        },

        close() {
          this.show = false;
          document.body.classList.remove("overflow-hidden");
        },

        setActiveLink(linkId) {
          this.currentMenuLinks.push(linkId);
        },

        removeActiveLink(linkId) {
          const idx = this.currentMenuLinks.indexOf(linkId);
          if (idx !== -1) this.currentMenuLinks.splice(idx, 1);
        },

        resetMenu() {
          this.currentMenuLinks = [];
        },

        scrollTop() {
          document.getElementById("menu-navigation").scrollTop = 0;
        },

        /**
         * Moves utility-bar children into the mobile toolbar.
         * In design mode, nodes are cloned to avoid breaking the editor.
         */
        moveElement() {
          const source = document.getElementById("utility-bar-mobile");
          const target = document.getElementById("main-nav-mobile-toolbar");

          if (!source || !target || target.children.length > 0) return;

          const count = source.children.length;
          for (let i = 0; i < count; i++) {
            if (Shopify.designMode) {
              target.appendChild(source.children[i].cloneNode(true));
            } else {
              target.appendChild(source.children[0]);
            }
          }
        },
      });


      /* ────────────────────────────────────────────────────────────────
       * DATA: xScrollMenu
       *
       * Horizontal scroll navigation for the desktop menu bar.
       * ──────────────────────────────────────────────────────────────── */
      Alpine.data("xScrollMenu", () => ({
        scrollAmount: 200,
        isAtStart: true,
        isAtEnd: false,
        isScrollable: false,
        _directionFlag: 1,

        init() {
          this._debouncedCheck = debounce(() => this.checkScroll(), 200);
          this.checkScroll();

          window.addEventListener("resize", () => this._debouncedCheck());

          if (window.Maximize.rtl) {
            this._directionFlag = -1;
          }
        },

        scrollLeft() {
          const currentScroll = this.$refs.menuList.scrollLeft * this._directionFlag;
          const amount = Math.min(this.scrollAmount, currentScroll);

          this.$refs.menuList.scrollBy({
            left: -amount * this._directionFlag,
            behavior: "smooth",
          });
          this._debouncedCheck();
        },

        scrollRight() {
          const menu = this.$refs.menuList;
          const currentScroll = menu.scrollLeft * this._directionFlag;
          const remaining = menu.scrollWidth - (currentScroll + menu.offsetWidth);
          const amount = Math.min(this.scrollAmount, remaining);

          menu.scrollBy({
            left: amount * this._directionFlag,
            behavior: "smooth",
          });
          this._debouncedCheck();
        },

        checkScroll() {
          if (window.innerWidth < 1023) return;

          const menu = this.$refs.menuList;
          this.isScrollable = menu.scrollWidth > menu.offsetWidth;

          if (window.Maximize.rtl) {
            const absScroll = Math.abs(menu.scrollLeft);
            this.isAtStart = absScroll <= 1;
            this.isAtEnd = absScroll >= menu.scrollWidth - menu.offsetWidth - 1;
          } else {
            this.isAtStart = menu.scrollLeft === 0;
            this.isAtEnd = menu.scrollLeft + menu.offsetWidth >= menu.scrollWidth;
          }
        },
      }));


      /* ────────────────────────────────────────────────────────────────
       * DATA: xMenuItem
       *
       * Positions dropdown menus relative to viewport edges.
       * Handles RTL layout mirroring.
       * ──────────────────────────────────────────────────────────────── */
      Alpine.data("xMenuItem", (rtl) => ({
        positionLeft: false,
        rtl: false,

        init() {
          this.rtl = rtl;
        },

        setPosition(el, level) {
          if (window.innerWidth < 1023) return;

          const menuItem = el.closest(".menu-item");
          const dropdown = menuItem.getElementsByClassName("toggle-menu")[0];
          if (!dropdown) return;
          const rect = menuItem.getBoundingClientRect();

          if (dropdown.classList.contains("horecalaan-tree-menu")) {
            const nav = menuItem.closest(".horecalaan-two-row-menu");
            const bounds = nav.getBoundingClientRect();
            const minLeft = Math.max(16, bounds.left);
            const maxRight = Math.min(window.innerWidth - 16, bounds.right);
            const width = Math.min(480, maxRight - minLeft);
            let left = Math.max(minLeft, Math.min(rect.left, maxRight - width));
            const hasChildren = dropdown.querySelector(".horecalaan-subcategories");
            this.positionLeft = false;
            dropdown.style.left = `${left - rect.left}px`;
            dropdown.style.right = "auto";
            return;
          }

          // Calculate available space
          const availableSpace = rtl
            ? rect.right - dropdown.offsetWidth * level
            : (window.innerWidth - rect.left) - dropdown.offsetWidth * level;

          if (level === 1) {
            const styles = getComputedStyle(menuItem);
            if (this.rtl) {
              dropdown.style.right = availableSpace < 0
                ? `${window.innerWidth + availableSpace - rect.right}px`
                : `${window.innerWidth - parseFloat(styles.paddingRight) - rect.right}px`;
            } else {
              dropdown.style.left = availableSpace < 0
                ? `${rect.left + availableSpace}px`
                : `${rect.left}px`;
            }
          } else {
            const subcategories = dropdown.querySelector(".horecalaan-subcategories");
            if (subcategories) {
              const dropdownRect = dropdown.getBoundingClientRect();
              this.positionLeft = rtl
                ? dropdownRect.left >= subcategories.offsetWidth + 16
                : dropdownRect.right + subcategories.offsetWidth > window.innerWidth - 16;
            } else {
              this.positionLeft = rtl ? availableSpace >= 0 : availableSpace < 0;
            }
          }
        },

        resizeWindow(el, level) {
          window.addEventListener("resize", () => {
            setTimeout(() => this.setPosition(el, level), 300);
          });

          const menuNav = el.closest(".main-menu-nav");
          if (menuNav) {
            menuNav.addEventListener("scroll", () => {
              Alpine.store("xHeaderMenu").hideMenu();
              this.setPosition(el, level);
            }, { passive: true });
          }
        },
      }));


      /* ────────────────────────────────────────────────────────────────
       * DATA: xFirstLoadItem
       *
       * Lazy-initializes menu items on first interaction.
       * ──────────────────────────────────────────────────────────────── */
      Alpine.data("xFirstLoadItem", () => ({
        isLoaded: false,

        firstLoad() {
          if (window.innerWidth < 1023) return;
          if (!this.isLoaded) this.isLoaded = true;
        },

        handleTouch(el) {
          if (el.dataset.touchItemEvent) return;

          el.dataset.touchItemEvent = "true";
          el.addEventListener("touchend", () => this.firstLoad());
        },
      }));


      /* ────────────────────────────────────────────────────────────────
       * STORE: xDrawerMenu
       *
       * Controls the desktop drawer-style mega menu panels.
       * ──────────────────────────────────────────────────────────────── */
      Alpine.store("xDrawerMenu", {
        menuOpen: null,

        handleMenuToggle(sectionId) {
          const header = Alpine.store("xHeaderMenu");
          const drawer = Alpine.store("xMaximizeDrawer");

          if (this.menuOpen === sectionId) {
            // Close current
            header.hideMenu(this.menuOpen);
            this.menuOpen = false;
            drawer.handleClose();
          } else {
            // Close previous, open new
            header.hideMenu("first-menu-item");
            if (this.menuOpen) header.hideMenu(this.menuOpen);

            this.menuOpen = sectionId;
            drawer.handleOpen(`#Section-${sectionId}`);
          }
        },

        handleCloseMenu(sectionId) {
          if (!this.menuOpen) return;

          this.menuOpen = null;
          Alpine.store("xMaximizeDrawer").handleClose();
          Alpine.store("xHeaderMenu").hideMenu(sectionId);
        },
      });
      Alpine.data("animationHover", () => ({
        isHovered: false,
        directionTranslate: null,
        getDirection(event) {
          const { top, height } = this.$el.getBoundingClientRect();
          return event.clientY - top > height / 2;
        },

        leave(toBottom) {
          this.directionTranslate = toBottom ? "translateY(101%)" : "translateY(-101%)",
          this.isHovered = false
        },

        handleEnter(event) {
          this.isHovered = true;
        },

        handleLeave(event) {
          this.isHovered = false;
          this.leave(this.getDirection(event));
        },
      }));

    }); // alpine:init
  }); // rAF
} // loadedScript guard
